import type { Translations } from '@/i18n/locales/en';
import { formatDateString } from '@/utils/datetime';
import { formatMoney } from '@/utils/money';
import {
  AiAssignTablesInputSchema,
  AiCancelReservationInputSchema,
  AiCloseTabInputSchema,
  AiCreateCustomerInputSchema,
  AiCreateReservationInputSchema,
  AiFireRoundInputSchema,
  AiManageCategoriesInputSchema,
  AiManageProductsInputSchema,
  AiMoveSessionInputSchema,
  AiSetItemAvailabilityInputSchema,
  AiSetPricesInputSchema,
  AiTakePaymentInputSchema,
  AiToggleOnlineOrderingInputSchema,
  AiToggleTableOrderingInputSchema,
  AiUpdateOpeningHoursInputSchema,
  AiUpdateProfileInputSchema,
  AiVoidItemInputSchema,
  type AiWriteToolName,
} from '@restaurant/api-contracts';

type AiStrings = Translations['aiAssistant'];

const namedAction = (
  s: AiStrings['summary'],
  item: { action: 'create' | 'update' | 'delete'; name?: string; currentName?: string },
): string => {
  if (item.action === 'create') return s.create(item.name ?? '');
  if (item.action === 'delete') return s.remove(item.currentName ?? '');
  return item.name && item.name !== item.currentName
    ? s.rename(item.currentName ?? '', item.name)
    : s.update(item.currentName ?? '');
};

export function describeWriteTool(
  toolName: AiWriteToolName,
  input: unknown,
  t: AiStrings,
  currency: string,
  locale: string,
): string[] {
  const s = t.summary;
  const money = (minor: number) => formatMoney(minor, currency, locale);
  switch (toolName) {
    case 'set_prices': {
      const parsed = AiSetPricesInputSchema.safeParse(input);
      return parsed.success
        ? parsed.data.items.map((i) => s.price(i.productName, money(i.price)))
        : [];
    }
    case 'set_item_availability': {
      const parsed = AiSetItemAvailabilityInputSchema.safeParse(input);
      return parsed.success
        ? parsed.data.items.map((i) =>
            i.inStock ? s.inStock(i.productName) : s.soldOut(i.productName),
          )
        : [];
    }
    case 'manage_products': {
      const parsed = AiManageProductsInputSchema.safeParse(input);
      return parsed.success ? parsed.data.items.map((i) => namedAction(s, i)) : [];
    }
    case 'manage_categories': {
      const parsed = AiManageCategoriesInputSchema.safeParse(input);
      return parsed.success ? parsed.data.items.map((i) => namedAction(s, i)) : [];
    }
    case 'create_reservation': {
      const parsed = AiCreateReservationInputSchema.safeParse(input);
      return parsed.success
        ? [
            s.reservation(
              parsed.data.guestName,
              formatDateString(parsed.data.date),
              parsed.data.time,
              parsed.data.partySize,
            ),
          ]
        : [];
    }
    case 'cancel_reservation': {
      const parsed = AiCancelReservationInputSchema.safeParse(input);
      return parsed.success ? [parsed.data.guestName] : [];
    }
    case 'assign_tables': {
      const parsed = AiAssignTablesInputSchema.safeParse(input);
      return parsed.success
        ? [s.tables(parsed.data.guestName, parsed.data.tableLabels.join(', '))]
        : [];
    }
    case 'create_customer': {
      const parsed = AiCreateCustomerInputSchema.safeParse(input);
      return parsed.success ? [`${parsed.data.firstName} ${parsed.data.lastName}`] : [];
    }
    case 'update_opening_hours': {
      const parsed = AiUpdateOpeningHoursInputSchema.safeParse(input);
      return parsed.success
        ? parsed.data.schedule.map((d) =>
            s.hoursDay(
              d.day,
              d.closed || !d.windows?.length
                ? s.closed
                : d.windows.map((w) => `${w.open}–${w.close}`).join(', '),
            ),
          )
        : [];
    }
    case 'update_profile': {
      const parsed = AiUpdateProfileInputSchema.safeParse(input);
      if (!parsed.success) return [];
      const fields = ['name', 'address', 'phone', 'email', 'description'] as const;
      return fields.flatMap((field) => {
        const value = parsed.data[field];
        return value ? [`${s.profileFields[field]}: ${value}`] : [];
      });
    }
    case 'toggle_online_ordering': {
      const parsed = AiToggleOnlineOrderingInputSchema.safeParse(input);
      return parsed.success
        ? [
            `${parsed.data.orderType === 'PICKUP' ? s.pickup : s.delivery}: ${
              parsed.data.enabled ? s.on : s.off
            }`,
          ]
        : [];
    }
    case 'toggle_table_ordering': {
      const parsed = AiToggleTableOrderingInputSchema.safeParse(input);
      return parsed.success ? [parsed.data.enabled ? s.on : s.off] : [];
    }
    case 'fire_round': {
      const parsed = AiFireRoundInputSchema.safeParse(input);
      return parsed.success ? [s.table(parsed.data.tableLabel)] : [];
    }
    case 'void_item': {
      const parsed = AiVoidItemInputSchema.safeParse(input);
      return parsed.success
        ? [parsed.data.itemName, ...(parsed.data.reason ? [parsed.data.reason] : [])]
        : [];
    }
    case 'take_payment': {
      const parsed = AiTakePaymentInputSchema.safeParse(input);
      if (!parsed.success) return [];
      const method = parsed.data.methodKind === 'CASH' ? s.cash : s.bankTransfer;
      return [
        s.table(parsed.data.tableLabel),
        `${method}: ${money(parsed.data.amountCents)}`,
        ...(parsed.data.tipCents ? [s.tip(money(parsed.data.tipCents))] : []),
      ];
    }
    case 'close_tab': {
      const parsed = AiCloseTabInputSchema.safeParse(input);
      return parsed.success ? [s.table(parsed.data.tableLabel)] : [];
    }
    case 'move_session': {
      const parsed = AiMoveSessionInputSchema.safeParse(input);
      return parsed.success ? [s.move(parsed.data.fromTableLabel, parsed.data.toTableLabel)] : [];
    }
  }
}
